import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ApolloProvider } from "@apollo/client"
import { BrowserRouter, Routes, Route, useLocation, type Location } from "react-router-dom"
import * as Sentry from "@sentry/react"
import { ThemeProvider } from "next-themes"
import { apolloClient } from "@/lib/apollo"
import { AuthProvider } from "@/contexts/AuthContext"
import { Administration } from "./pages/Administration"
import Index from "./pages/Index"
import NotFound from "./pages/NotFound"
import Login from "./pages/auth/Login"
import Register from "./pages/auth/Register"
import ForgotPassword from "./pages/auth/ForgotPassword"
import ChangePassword from "./pages/auth/ChangePassword"
import UserManagement from "./pages/UserManagement"
import Profile from "./pages/Profile"
import EditProfile from "./pages/EditProfile"
import MyTournaments from "./pages/MyTournaments"
import MyStats from "./pages/MyStats"
import { CreateStore } from "./pages/CreateStore"
import { EditStore } from "./pages/EditStore"
import Ranking from "./pages/Ranking"
import SeasonDetail from "./pages/SeasonDetail"
import Eventos from "./pages/Eventos"
import Tiendas from "./pages/Tiendas"
import StoreDetail from "./pages/StoreDetail"
import StoreTournaments from "./pages/StoreTournaments"
import TournamentDetail from "./pages/Tournament/Detail/TournamentDetail"
import TournamentPlayers from "./pages/TournamentPlayers"
import TournamentPlayersPrint from "./pages/TournamentPlayersPrint"
import TournamentRounds from "./pages/TournamentRounds"
import TournamentReport from "./pages/TournamentReport"
import { TournamentAdminView } from "./pages/TournamentAdmin"
import TournamentRoundView from "./pages/TournamentRoundView"
import TournamentRoundViewBracketTop from "./pages/Tournament/TournamentTop/Bracket"
import TournamentRoundStanding from "./pages/TournamentRoundStanding"
import CreateTournament from "./pages/CreateTournament"
import { AdminPlayerStats } from "./pages/AdminPlayerStats"
// import BlogStanding from "./pages/Blog/Standing/Standing"
import BlogTerms from "./pages/Blog/Terms"
import FAQ from "./pages/FAQ"
import { useEffect } from "react"

const queryClient = new QueryClient()

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

function AppRoutes({ location }: { location: Location }) {
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<Index />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/change-passwd/:personId/:code" element={<ChangePassword />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/profile/edit" element={<EditProfile />} />
      <Route path="/profile/tournaments" element={<MyTournaments />} />
      <Route path="/profile/stats" element={<MyStats />} />
      <Route path="/stores/create" element={<CreateStore />} />
      <Route path="/stores/register" element={<CreateStore />} />
      <Route path="/stores/:id/edit" element={<EditStore />} />
      <Route path="/ranking" element={<Ranking />} />
      <Route path="/ranking/season/:gameId/:seasonId/:slug" element={<SeasonDetail />} />
      <Route path="/tournaments" element={<Eventos />} />
      <Route path="/admin" element={<Administration />} />
      <Route path="/admin/player-stats" element={<AdminPlayerStats />} />
      <Route path="/admin/player-stats/:playerCode" element={<AdminPlayerStats />} />
      <Route path="/admin/:section" element={<Administration />} />
      <Route path="/admin/users" element={<UserManagement />} />
      <Route path="/tournaments/create" element={<CreateTournament />} />
      <Route path="/stores" element={<Tiendas />} />
      <Route path="/store/detail/:storeId/:slug" element={<StoreDetail />} />
      <Route path="/store/tournaments/:storeId/:slug" element={<StoreTournaments />} />
      <Route path="/tournament/detail/:tournamentId/:slug" element={<TournamentDetail />} />
      <Route path="/tournament/players/:tournamentId/:slug" element={<TournamentPlayers />} />
      <Route path="/tournament/players/print/:tournamentId" element={<TournamentPlayersPrint />} />
      <Route path="/tournament/rounds/:tournamentId/:slug" element={<TournamentRounds />} />
      <Route path="/tournament/report/:tournamentId/:slug" element={<TournamentReport />} />
      <Route path="/tournament/top" element={<TournamentRoundViewBracketTop />} />
      <Route
        path="/tournament/round/:roundId/:slug/:tournamentId"
        element={<TournamentRoundView />}
      />
      <Route path="/tournament/admin/:tournamentId" element={<TournamentAdminView />} />
      <Route
        path="/tournament/round/:roundId/:slug/:pdf/:tournamentId"
        element={<TournamentRoundView />}
      />
      <Route
        path="/tournament/round/:roundId/:slug/pdf/:tables/:tournamentId"
        element={<TournamentRoundView />}
      />
      <Route
        path="/tournament/standing/:roundId/:slug/:tournamentId"
        element={<TournamentRoundStanding />}
      />
      <Route
        path="/tournament/standing/:roundId/:slug/:pdf/:tournamentId"
        element={<TournamentRoundStanding />}
      />
      {/* <Route path="/blog/standing" element={<BlogStanding />} /> */}
      <Route path="/terms-conditions" element={<BlogTerms />} />
      <Route path="/faq" element={<FAQ />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function RouterContent() {
  const location = useLocation()
  return (
    <>
      <ScrollToTop />
      <AppRoutes location={location} />
    </>
  )
}

const App = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-6">
          <h2 className="text-2xl font-bold text-destructive">Algo salió mal</h2>
          <p className="text-muted-foreground">
            Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado.
          </p>
          <button
            onClick={resetError}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    )}
    showDialog
  >
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            storageKey="myl-theme"
          >
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <RouterContent />
              </BrowserRouter>
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ApolloProvider>
  </Sentry.ErrorBoundary>
)

export default App
